import { useEffect, useState } from 'react';
import { Banner, Button, Icon } from '../../components';
import type { AppVersionPolicy } from '../../lib/api/types';
import { APP_VERSION, platform } from '../../lib/device';
import { useI18n } from '../../lib/i18n';
import { useServices } from '../../app/services';
import { applyUpdate, markUpdateRequired } from '../../lib/pwa';
import { AuthLayout } from './AuthLayout';

/** Below the minimum supported version the app refuses to run or sync (ADR-0007). */
export function UpdateRequiredPage({ policy }: { policy: AppVersionPolicy | null }) {
  const { t } = useI18n();
  const apk = platform() === 'android' ? policy?.androidApkUrl : null;
  const [busy, setBusy] = useState(false);
  // Nothing can be edited behind this screen: a waiting service worker is applied at once (C18).
  useEffect(() => markUpdateRequired(), []);
  return (
    <AuthLayout>
      <div className="flex flex-col items-center gap-4 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-warning-soft text-warning">
          <Icon name="download" size={28} />
        </span>
        <h2 className="text-xl font-bold">{t('ui.version.required')}</h2>
        <p className="text-muted">{t('ui.version.requiredBody', { current: APP_VERSION, minimum: policy?.minimum ?? '' })}</p>
        {apk ? (
          <a href={apk} className="w-full">
            <Button size="lg" block icon="download">
              {t('ui.version.downloadApk')}
            </Button>
          </a>
        ) : (
          <Button
            size="lg"
            block
            icon="refresh"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              await applyUpdate();
            }}
          >
            {t('ui.version.reload')}
          </Button>
        )}
      </div>
    </AuthLayout>
  );
}

export function UpdateAvailableBanner({ policy }: { policy: AppVersionPolicy | null }) {
  const { t } = useI18n();
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  const apk = platform() === 'android' ? policy?.androidApkUrl : null;
  return (
    <Banner
      tone="info"
      onDismiss={() => setDismissed(true)}
      dismissLabel={t('ui.common.dismiss')}
      action={
        apk ? (
          <a href={apk} className="font-semibold underline">
            {t('ui.version.downloadApk')}
          </a>
        ) : (
          <button type="button" className="font-semibold underline" onClick={() => void applyUpdate()}>
            {t('ui.version.reload')}
          </button>
        )
      }
    >
      {t('ui.version.available', { latest: policy?.latest ?? '' })}
    </Banner>
  );
}

/** Offline for longer than the allowed period: sign in online again; drafts are kept but locked (ADR-0021). */
export function OfflineReauthPage({ onRetry }: { onRetry: () => void }) {
  const { t } = useI18n();
  const { auth } = useServices();
  return (
    <AuthLayout>
      <div className="flex flex-col items-center gap-4 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-warning-soft text-warning">
          <Icon name="wifiOff" size={28} />
        </span>
        <h2 className="text-xl font-bold">{t('ui.offline.reauthTitle')}</h2>
        <p className="text-muted">{t('ui.offline.reauthBody')}</p>
        <Button size="lg" block icon="refresh" onClick={onRetry}>
          {t('ui.common.retry')}
        </Button>
        <Button variant="ghost" onClick={() => auth.signOut()}>
          {t('ui.offline.signInAgain')}
        </Button>
      </div>
    </AuthLayout>
  );
}
